import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

import api from './axiosConfig';

const Profile = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    role: user?.rol || '',
    bio: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Debug: Log user info on mount
  React.useEffect(() => {
    console.log('📊 Profile - User Data:', user);
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put(`/auth/users/${user.id}`, { email: formData.email });
      setMessage({ type: 'success', text: 'Cambios guardados correctamente' });
      
      // Actualizar usuario en localStorage
      const updatedUser = { ...user, email: formData.email };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al actualizar perfil',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getMemberSince = () => {
    // Por ahora retornamos una fecha fija, idealmente vendría del backend
    return 'Oct 2023';
  };

  const getActiveProjects = () => {
    // Por ahora retornamos un número fijo, idealmente vendría del backend
    return 12;
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {/* Header */}
      <motion.header 
        className="bg-surface-light dark:bg-surface-dark shadow-sm border-b border-border-light dark:border-border-dark"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">Mi Perfil</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">Administra tu información personal y configuración de cuenta en la plataforma.</p>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Mensaje de éxito/error */}
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}
            >
              <span
                className={`material-icons-round ${
                  message.type === 'success'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {message.type === 'success' ? 'check_circle' : 'error'}
              </span>
              <p
                className={`text-sm font-medium ${
                  message.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}
              >
                {message.text}
              </p>
            </motion.div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Profile Card */}
            <motion.div 
              className="lg:col-span-1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col items-center text-center">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary/20 bg-slate-100 dark:bg-slate-800 mb-4 flex items-center justify-center">
                    <span className="text-5xl font-bold text-slate-600 dark:text-slate-300">
                      {user.username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <button className="absolute bottom-2 right-0 bg-primary text-white p-2 rounded-full shadow-lg hover:bg-primary/90 transition-transform hover:scale-105 flex items-center justify-center">
                    <span className="material-icons-round text-sm">photo_camera</span>
                  </button>
                </div>
                
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">{user.username}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                  {user.rol === 'SuperAdmin'
                    ? 'Super Administrador'
                    : user.rol === 'ADMIN_CEDIA'
                    ? 'Admin CEDIA'
                    : user.rol === 'Administrador'
                    ? 'Administrador'
                    : user.rol === 'ADMIN_IES'
                    ? 'Admin IES'
                    : user.rol === 'INVESTIGADOR'
                    ? 'Investigador'
                    : user.rol === 'CONSULTA'
                    ? 'Consulta'
                    : 'Usuario'}
                </p>
                
                <div className="w-full pt-6 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-500 dark:text-slate-400">Proyectos Activos</span>
                    <span className="font-bold text-primary">{getActiveProjects()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Miembro desde</span>
                    <span className="font-medium">{getMemberSince()}</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Edit Form */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="p-6 border-b border-slate-200 dark:border-slate-800">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Editar Información</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Actualiza tus datos de contacto y rol institucional.</p>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-slate-700 dark:text-slate-300" htmlFor="username">
                        Nombre de usuario
                      </label>
                      <div className="relative">
                        <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">person</span>
                        <input
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none"
                          id="username"
                          name="username"
                          type="text"
                          value={formData.username}
                          onChange={handleChange}
                          disabled
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-slate-700 dark:text-slate-300" htmlFor="role">
                        Rol
                      </label>
                      <div className="relative">
                        <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">badge</span>
                        <input
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 transition-all outline-none"
                          id="role"
                          name="role"
                          type="text"
                          value={formData.role}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300" htmlFor="email">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xl">mail</span>
                      <input
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none"
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300" htmlFor="bio">
                      Biografía Profesional (Opcional)
                    </label>
                    <textarea
                      className="w-full p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none resize-none"
                      id="bio"
                      name="bio"
                      placeholder="Cuéntanos un poco sobre tu área de investigación..."
                      rows="4"
                      value={formData.bio}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                  
                  <div className="pt-4 flex items-center justify-end gap-4 border-t border-slate-200 dark:border-slate-800">
                    <button
                      className="px-6 py-2.5 rounded-lg text-sm font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      type="button"
                      onClick={() => {
                        setFormData({
                          username: user.username,
                          email: user.email,
                          role: user.rol,
                          bio: ''
                        });
                        setMessage({ type: '', text: '' });
                      }}
                      disabled={isSaving}
                    >
                      Cancelar
                    </button>
                    <button
                      className="px-8 py-2.5 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      type="submit"
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Guardando...</span>
                        </>
                      ) : (
                        'Guardar Cambios'
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              {/* Secondary Settings */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      <span className="material-icons-round">lock</span>
                    </div>
                    <h4 className="font-bold">Seguridad</h4>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Actualiza tu contraseña y configura la autenticación de dos factores.
                  </p>
                  <button className="text-primary text-sm font-bold hover:underline flex items-center gap-1">
                    Gestionar seguridad <span className="material-icons-round text-sm">arrow_forward</span>
                  </button>
                </div>
                
                <div className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      <span className="material-icons-round">notifications</span>
                    </div>
                    <h4 className="font-bold">Notificaciones</h4>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Controla qué alertas recibes sobre tus proyectos y colaboraciones.
                  </p>
                  <button className="text-primary text-sm font-bold hover:underline flex items-center gap-1">
                    Preferencias de avisos <span className="material-icons-round text-sm">arrow_forward</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
